import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Typography,
  TextField,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { Delete, Close, AddCircleOutline } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CategorySearchModal from "./CategorySelectionModal";
import { fAlertToast, hasNonEnterStatus } from "../../../Utility/Utilitys";
import CustomDatagrid from "../Components/CustomDatagrid";
import { confirmAlert } from "react-confirm-alert";
import SideNavToolbar from "../Components/SidenavToolbar";
import SideNavHeader from "../Components/SideNavHeader";
import ChipCsv from "../Components/ChipCsv";
import { PAP_Pages_Delete, PAP_Pages_Getrow, PAP_Pages_Insert, PAP_Pages_Update } from "../../../Server/ApiV2";

export default function CategorySelectionSideNav({
  open,
  onClose,
  selectedPromo,
  pageType,
  userCd,
  loadSummary,
}) {
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:900px)");
  const [pages, setPages] = useState([]);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState({});

  const getPageRows = async () => {
    try {
      const response = await PAP_Pages_Getrow({
        TransId: selectedPromo?.TransId || "",
      });
      if (response.status === 200) {
        setPages(response?.data?.recordset ?? []);
      }
    } catch (err) {
      fAlertToast("FAILED", err?.response?.data?.message || err?.message);
    }
  };

  useEffect(() => {
    if (open) getPageRows();
  }, [open]);

  const handleAddPages = async () => {
    try {
      const res = await PAP_Pages_Insert({
        TransId: selectedPromo?.TransId || "",
      });
      if (res.status === 200) {
        await getPageRows();
        await loadSummary();
      }
    } catch (err) {
      fAlertToast("FAILED", err?.response?.data?.message || err?.message);
    }
  };

  const handleDeleteRow = async (pageId) => {
    try {
      const res = await PAP_Pages_Delete({
        TransId: selectedPromo?.TransId || "",
        PageId: pageId || "",
      });
      if (res.status === 200 && res?.data?.status === "Success") {
        fAlertToast("SUCCESS", "Page row deleted");
        await getPageRows();
        await loadSummary();
      }
    } catch (err) {
      fAlertToast("FAILED", err?.response?.data?.message || err?.message);
    }
  };

  const handleSave = async () => {
    const hasEmpty = pages.some((p) => String(p.PageNo ?? "").trim() === "");
    if (hasEmpty) {
      fAlertToast("FAILED", "Please fill Page No for all rows before saving.");
      return;
    }
    const detail = pages.map((p) => ({
      PageId: p.PageId,
      PageNo: Number(p.PageNo) || 0,
    }));

    if (detail.length === 0) {
      fAlertToast("INFO", "No valid pages to update.");
      return;
    }
    try {
      const res = await PAP_Pages_Update({
        TransId: selectedPromo?.TransId ?? "",
        detail: detail,
      });

      if (res.status === 200) {
        fAlertToast("SUCCESS", "Pages updated successfully");
        await getPageRows();
        await loadSummary();
      }
    } catch (err) {
      fAlertToast("FAILED", err?.response?.data?.message || err?.message);
    }
  };

  const handleRowClick = (page, event) => {
    const isDup =
      pages.filter(
        (p) => String(p.PageNo ?? "") === String(page.PageNo ?? "")
      ).length > 1;

    if (isDup) {
      fAlertToast("FAILED", `Page No ${page.PageNo} already exists.`);
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      return;
    }

    setIsCategoryModalOpen(true);
    setSelectedPage(page);
  };
  const columns = useMemo(
    () => [
      {
        key: "PageNo",
        header: "P.No",
        width: 25,
        sortable: true,
        filterable: true,
        align: "center",
        render: (row) => {
          const isDuplicate =
            row._touched &&
            row.PageNo !== "" &&
            pages.filter(
              (p) => String(p.PageNo ?? "") === String(row.PageNo ?? "")
            ).length > 1;

          return (
            <TextField
              size="small"
              type="text" // <-- IMPORTANT (prevents e, -, .)
              variant="outlined"
              value={row?.PageNo ?? ""}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "");

                setPages((prev) =>
                  prev.map((r) =>
                    r.PageId === row.PageId ? { ...r, PageNo: val } : r
                  )
                );
              }}
              onBlur={() => {
                let isDup = pages.filter(
                  (p) => String(p.PageNo ?? "") === String(row.PageNo ?? "")
                ).length > 1;
                if (isDup) {
                  fAlertToast("FAILED", `Page No ${row.PageNo} already exists.`);
                }
                setPages((prev) =>
                  prev.map((r) =>
                    r.PageId === row.PageId ? { ...r, _touched: true } : r
                  )
                );
              }}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", ".", ","].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              autoComplete="off"
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                style: { textAlign: "center" },
              }}
            />
          );
        },
      },
      {
        key: "CategoryName",
        header: "Category",
        width: 200,
        sortable: true,
        filterable: true,
        align: "left",
        render: (row) => <ChipCsv value={row?.CategoryName} />,
      },
      {
        key: "BrandName",
        header: "Brand",
        width: 200,
        sortable: true,
        filterable: true,
        align: "left",
        render: (row) => <ChipCsv value={row?.BrandName} />,
      },
      {
        key: "BuyerName",
        header: "Buyer",
        width: 130,
        sortable: true,
        filterable: true,
        align: "left",
        render: (row) => <ChipCsv value={row?.BuyerName} />,
      },
      {
        key: "PackingName",
        header: "Packing",
        width: 130,
        sortable: true,
        filterable: true,
        align: "left",
        render: (row) => <ChipCsv value={row?.PackingName} />,
      },
      {
        key: "NoOfItems",
        header: "No Of Items",
        width: 70,
        sortable: true,
        filterable: true,
        align: "center",
        numeric: true,
      },
      {
        key: "",
        header: "#",
        width: 10,
        align: "center",
        render: (row) => (
          <Tooltip title="Delete page">
            <IconButton
              size="small"
              disabled={hasNonEnterStatus(selectedPromo)}
              onClick={() => {
                confirmAlert({
                  title: "Delete",
                  message: "Are you sure you want to delete.",
                  buttons: [
                    {
                      className: "alertBtn",
                      name: "SaveYes",
                      label: "Yes",
                      onClick: () => handleDeleteRow(row?.PageId),
                    },
                    {
                      className: "alertBtn",
                      label: "No",
                      onClick: () => "",
                    },
                  ],
                });
              }}
              aria-label="delete"
              color="error"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [pages]
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      PaperProps={{
        sx: {
          mt: 5,
          width: { xs: "100%", sm: "85%", md: "80%" },
          height: "100vh",
        },
      }}
    >
      <Box
        sx={{
          background: "linear-gradient(135deg, #1e88e5 0%, #673ab7 100%)",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1.2,
          borderBottom: "1px solid #1565c0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "self-start",
          }}
        >
          <IconButton
            edge="end"
            onClick={() => {
              onClose();
              setPages([]);
            }}
            aria-label="close"
            sx={{
              fontSize: "20px !important",
              color: "#ffffff",
              backgroundColor: "rgba(255,255,255,0.15)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.25)",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ fontWeight: 500, marginLeft: "22px", marginTop: "4px" }}
          >
            Pages Detail
          </Typography>
        </Box>
        <IconButton
          onClick={() => {
            onClose();
            setPages([]);
          }}
          sx={{
            color: "#fff",
            backgroundColor: "rgba(255,255,255,0.15)",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.25)",
            },
          }}
        >
          <Close />
        </IconButton>
      </Box>
      <SideNavToolbar
        userCd={userCd}
        transId={selectedPromo?.TransId || ""}
        selectedRow={selectedPromo}
        loadSummary={loadSummary}
      />
      <Box sx={{ px: 1.5 }}>
        <SideNavHeader selectedRow={selectedPromo} pageType={pageType} />
      </Box>
      <Box sx={{ p: { xs: "4px 8px", sm: 1.5 } }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <Button
            startIcon={<AddCircleOutline />}
            color="primary"
            variant="text"
            onClick={handleAddPages}
            disabled={hasNonEnterStatus(selectedPromo)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              my: 0.5,
              "&.Mui-disabled": {
                backgroundColor: "rgba(0,0,0,0.05)",
                color: "rgba(0,0,0,0.3)",
                borderRadius: "4px",
                cursor: "not-allowed",
              },
            }}
          >
            Add new pages
          </Button>
        </Box>
        <CustomDatagrid
          pageType={pageType}
          columns={columns}
          rows={pages}
          dense={true}
          onRowClick={(row, e) => handleRowClick(row, e)}
          height={{
            xs: "37vh",
            sm: "43vh",
            md: "49vh",
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1,
            mt: 1,
          }}
        >
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={hasNonEnterStatus(selectedPromo)}
          >
            Save
          </Button>
        </Box>
      </Box>
      <CategorySearchModal
        open={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        selectedPromo={selectedPromo}
        selectedPage={selectedPage}
        pageType={pageType}
        isMobile={isMobile}
        isTablet={isTablet}
        loadPages={getPageRows}
        loadSummary={loadSummary}
      />
    </Drawer>
  );
}
